import {
  ModalCloseButton as ChakraModalCloseButton,
  ModalHeader as ChakraModalHeader,
} from '@chakra-ui/react'
import * as React from 'react'

import { BgColorProvider, useBgColorCtx } from '~theme/BgColorProvider'
import { shouldUseHighFgContrast } from '~theme/fgColor'
import { shadows } from '~ui/styleGuide/shadows'
import { getRightSpacingValue, getSpacingValue } from '~ui/styleGuide/spaces'
import { THEME_KEY_ATTRIBUTE } from '~ui/themeDesignTools/config/themeDesignTools.config'

import { useModalSettingsCtx } from '../context/ModalSettingsProvider'
import { TModalHeaderFooterProps } from './ModalFooter'

type TProps = TModalHeaderFooterProps & {
  /**
   * unique id to access the modal header from anywhere
   */
  id?: PropsOf<typeof ChakraModalHeader>['id']
  /**
   * option to render the close icon in the top-right corner
   * the icon is positioned absolutely & might overlap some content
   */
  withCloseButton?: boolean
}

/**
 * actual implementation
 * used inside a BgProviderWrapper bellow
 */
const ModalHeaderComponent: React.FC<TProps> = ({
  id,
  padding,
  children,
  shadow = `none`,
  bgColor,
  withCloseButton = false,
}) => {
  const { bgColorValue, fgColors } = useBgColorCtx()
  const { radiusValue, headerPadding, paddingHorizontalValue } = useModalSettingsCtx()

  const shadowValue = shadows.outer[shadow]
  const paddingValue = padding ? getSpacingValue(padding) : headerPadding
  const paddingRightValue = padding ? getRightSpacingValue(padding) : paddingHorizontalValue

  const hasBgColorSet = bgColor !== undefined
  const themeDesignProps = { [THEME_KEY_ATTRIBUTE]: bgColor }

  return (
    <ChakraModalHeader
      {...themeDesignProps}
      id={id}
      // TODO zIndex
      zIndex={1}
      shadow={shadowValue}
      position={`relative`}
      bgColor={hasBgColorSet ? bgColorValue : undefined}
      padding={paddingValue}
      borderTopRadius={radiusValue}
    >
      {children}
      {withCloseButton && (
        <ChakraModalCloseButton
          top={`50%`}
          cursor={`pointer`}
          right={paddingRightValue}
          position={`absolute`}
          color={
            hasBgColorSet && shouldUseHighFgContrast(bgColor) ? fgColors.high : fgColors.normal
          }
          transform={`translateY(-50%)`}
          bgColor={bgColorValue}
        />
      )}
    </ChakraModalHeader>
  )
}

/**
 * component for rendering the top section of the Modal
 * wrapping the Chakra’s ModalHeader
 * can be rendered with close button
 * and sync padding with the rest of the Modal if not overridden
 */
export const ModalHeader: React.FC<TProps> = (props) => {
  // this is just a BgColorProvider wrapper, the actual component is above
  return (
    <BgColorProvider bgColor={props.bgColor ?? null}>
      <ModalHeaderComponent {...props} />
    </BgColorProvider>
  )
}
